// src/pages/dashboard/Reports.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  Avatar,
  Divider,
  LinearProgress,
} from "@mui/material";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";

import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";

import { get, onValue, off, ref } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";
import { useAuthContext } from "../../contexts/AuthContext";

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmt(n) {
  return Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtShort(n) {
  if (n >= 1000) return `R$${(n / 1000).toFixed(1)}k`;
  return `R$${Number(n || 0).toFixed(0)}`;
}

function dayKey(ms) {
  const d = new Date(Number(ms));
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function weekKey(ms) {
  const d = new Date(Number(ms));
  const startOfYear = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
  return `S${week}`;
}

const PERIODS = [
  { label: "7 dias", value: 7 },
  { label: "30 dias", value: 30 },
  { label: "90 dias", value: 90 },
  { label: "Tudo", value: 0 },
];

const glass = {
  p: { xs: 2, md: 2.5 },
  borderRadius: "20px",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.10)",
  backdropFilter: "blur(18px)",
};

const CHART_COLORS = {
  primary: "#40c4ff",
  green: "#69f0ae",
  yellow: "#ffd54f",
  purple: "#ce93d8",
  pix: "#69f0ae",
  credit: "#40c4ff",
  other: "#ffd54f",
};

const PIE_COLORS = ["#69f0ae", "#40c4ff", "#ffd54f", "#ce93d8", "#ff8a65"];

// ─── Tooltip customizado para recharts ────────────────────────────────────────

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      background: "rgba(10,20,40,0.95)",
      border: "1px solid rgba(255,255,255,0.14)",
      borderRadius: "10px",
      p: 1.5,
      minWidth: 130,
    }}>
      <Typography sx={{ fontSize: 11, opacity: 0.6, mb: 0.5 }}>{label}</Typography>
      {payload.map((p) => (
        <Stack key={p.name} direction="row" alignItems="center" spacing={0.8}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            {p.name === "Receita" ? fmt(p.value) : p.value}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

// ─── KPI card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, icon, color, bg, sub }) {
  return (
    <Paper sx={{ ...glass, p: { xs: 1.8, md: 2.2 } }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography sx={{ fontSize: 11, opacity: 0.55, textTransform: "uppercase", letterSpacing: 1 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 900, lineHeight: 1.2, mt: 0.4, color }}>
            {value}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: 11, opacity: 0.5, mt: 0.4 }}>{sub}</Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: bg, color, width: 40, height: 40, flexShrink: 0 }}>
          {icon}
        </Avatar>
      </Stack>
    </Paper>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function Reports() {
  const { user } = useAuthContext();
  const organizerId = user?.uid;

  const [period, setPeriod] = useState(30);
  const [eventFilter, setEventFilter] = useState("all");
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [myEvents, setMyEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tickets, setTickets] = useState([]);

  // ── carrega eventos ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!organizerId) { setLoadingEvents(false); return; }
    const evRef = ref(rtdb, "events");
    const handler = (snap) => {
      const val = snap.val() || {};
      const list = Object.entries(val)
        .map(([id, d]) => ({ id, ...d }))
        .filter((e) => e.organizerId === organizerId);
      setMyEvents(list);
      setLoadingEvents(false);
    };
    onValue(evRef, handler, () => { setMyEvents([]); setLoadingEvents(false); });
    return () => off(evRef, "value", handler);
  }, [organizerId]);

  // ── carrega pedidos ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!organizerId || myEvents.length === 0) {
      setOrders([]);
      setLoadingOrders(false);
      return;
    }
    const eventIds = new Set(myEvents.map((e) => e.id));
    setLoadingOrders(true);
    get(ref(rtdb, "orders")).then((snap) => {
      if (!snap.exists()) { setOrders([]); return; }
      const all = Object.values(snap.val() || {});
      setOrders(all.filter((o) => eventIds.has(o.eventId)));
    }).catch(() => setOrders([])).finally(() => setLoadingOrders(false));
  }, [myEvents, organizerId]);

  const loading = loadingEvents || loadingOrders;

  // ── filtros ───────────────────────────────────────────────────────────────
  const periodMs = period > 0 ? Date.now() - period * 86_400_000 : 0;

  const filtered = useMemo(() => {
    let list = orders;
    if (periodMs) list = list.filter((o) => (o.createdAt || 0) >= periodMs);
    if (eventFilter !== "all") list = list.filter((o) => o.eventId === eventFilter);
    return list.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  }, [orders, periodMs, eventFilter]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const revenue = filtered.reduce((s, o) => s + Number(o.summary?.total || 0), 0);
    const tickets = filtered.reduce((s, o) => s + Number(o.ticketCount || 0), 0);
    const totalOrders = filtered.length;
    const avgTicket = totalOrders > 0 ? revenue / totalOrders : 0;

    // check-in
    const checkedIn = filtered.reduce((s, o) => {
      const checked = (o.items || []).reduce((si, item) => si + Number(item.checkedIn || 0), 0);
      return s + checked;
    }, 0);
    const checkinRate = tickets > 0 ? Math.round((checkedIn / tickets) * 100) : 0;

    return { revenue, tickets, totalOrders, avgTicket, checkedIn, checkinRate };
  }, [filtered]);

  // ── receita ao longo do tempo ─────────────────────────────────────────────
  const revenueOverTime = useMemo(() => {
    const useWeek = period === 90 || period === 0;
    const map = {};
    for (const o of filtered) {
      const key = useWeek ? weekKey(o.createdAt) : dayKey(o.createdAt);
      if (!map[key]) map[key] = { label: key, Receita: 0, Ingressos: 0 };
      map[key].Receita += Number(o.summary?.total || 0);
      map[key].Ingressos += Number(o.ticketCount || 0);
    }
    return Object.values(map);
  }, [filtered, period]);

  // ── por evento ────────────────────────────────────────────────────────────
  const byEvent = useMemo(() => {
    const map = {};
    for (const o of filtered) {
      if (!map[o.eventId]) {
        map[o.eventId] = { id: o.eventId, title: o.eventTitle || "—", revenue: 0, tickets: 0, orders: 0 };
      }
      map[o.eventId].revenue += Number(o.summary?.total || 0);
      map[o.eventId].tickets += Number(o.ticketCount || 0);
      map[o.eventId].orders += 1;
    }
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [filtered]);

  const maxRevenue = byEvent[0]?.revenue || 0;

  // ── por método de pagamento ───────────────────────────────────────────────
  const byPayment = useMemo(() => {
    const map = {};
    for (const o of filtered) {
      const method = o.paymentMethod === "pix" ? "Pix"
        : o.paymentMethod === "credit" ? "Cartão"
        : o.paymentMethod === "boleto" ? "Boleto"
        : "Outro";
      if (!map[method]) map[method] = { name: method, value: 0, revenue: 0 };
      map[method].value += 1;
      map[method].revenue += Number(o.summary?.total || 0);
    }
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [filtered]);

  // ── por tipo de ingresso ──────────────────────────────────────────────────
  const byTicketType = useMemo(() => {
    const map = {};
    for (const o of filtered) {
      for (const item of o.items || []) {
        const key = item.name || "Desconhecido";
        if (!map[key]) map[key] = { name: key, qty: 0, revenue: 0 };
        map[key].qty += Number(item.quantity || 0);
        map[key].revenue += Number(item.lineTotal || 0);
      }
    }
    return Object.values(map).sort((a, b) => b.qty - a.qty);
  }, [filtered]);

  const maxQty = byTicketType[0]?.qty || 0;

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Header ── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.5)" }}>
            ANÁLISE & DADOS
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Relatórios
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.2} flexWrap="wrap">
          {/* filtro de evento */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              sx={selectSx}
            >
              <MenuItem value="all">Todos os eventos</MenuItem>
              {myEvents.map((ev) => (
                <MenuItem key={ev.id} value={ev.id}>{ev.title}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* filtro de período */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value))}
              sx={selectSx}
            >
              {PERIODS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ py: 16, display: "grid", placeItems: "center" }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ py: 16, display: "grid", placeItems: "center", opacity: 0.5 }}>
          <ReceiptLongRoundedIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography>Nenhum pedido no período selecionado</Typography>
        </Box>
      ) : (
        <Stack spacing={3}>
          {/* ── KPIs ── */}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
            gap: { xs: 1.5, md: 2 },
          }}>
            <KpiCard
              label="Receita bruta"
              value={fmt(kpis.revenue)}
              icon={<TrendingUpRoundedIcon />}
              color="#40c4ff"
              bg="rgba(0,180,255,0.15)"
            />
            <KpiCard
              label="Ingressos vendidos"
              value={kpis.tickets}
              icon={<ConfirmationNumberRoundedIcon />}
              color="#69f0ae"
              bg="rgba(0,255,180,0.15)"
            />
            <KpiCard
              label="Pedidos"
              value={kpis.totalOrders}
              icon={<ReceiptLongRoundedIcon />}
              color="#ffd54f"
              bg="rgba(255,200,0,0.15)"
            />
            <KpiCard
              label="Ticket médio"
              value={fmt(kpis.avgTicket)}
              icon={<AccountBalanceWalletRoundedIcon />}
              color="#ce93d8"
              bg="rgba(206,147,216,0.15)"
            />
            <KpiCard
              label="Check-ins"
              value={`${kpis.checkinRate}%`}
              icon={<QrCodeScannerRoundedIcon />}
              color="#ff8a65"
              bg="rgba(255,138,101,0.15)"
              sub={`${kpis.checkedIn} de ${kpis.tickets} ingressos`}
            />
          </Box>

          {/* ── Receita ao longo do tempo ── */}
          <Paper sx={glass}>
            <Typography sx={{ fontWeight: 800, mb: 2 }}>Receita ao longo do tempo</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={revenueOverTime} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#40c4ff" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#40c4ff" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradTickets" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#69f0ae" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#69f0ae" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="label"
                  tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  yAxisId="left"
                  tickFormatter={fmtShort}
                  tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  width={52}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  width={30}
                />
                <RTooltip content={<CustomTooltip />} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="Receita"
                  stroke="#40c4ff"
                  strokeWidth={2}
                  fill="url(#gradRevenue)"
                  dot={false}
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="Ingressos"
                  stroke="#69f0ae"
                  strokeWidth={2}
                  fill="url(#gradTickets)"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
            <Stack direction="row" spacing={2} sx={{ mt: 1.5, opacity: 0.7 }}>
              <Stack direction="row" spacing={0.6} alignItems="center">
                <Box sx={{ width: 12, height: 3, borderRadius: 2, bgcolor: "#40c4ff" }} />
                <Typography sx={{ fontSize: 11 }}>Receita</Typography>
              </Stack>
              <Stack direction="row" spacing={0.6} alignItems="center">
                <Box sx={{ width: 12, height: 3, borderRadius: 2, bgcolor: "#69f0ae" }} />
                <Typography sx={{ fontSize: 11 }}>Ingressos</Typography>
              </Stack>
            </Stack>
          </Paper>

          {/* ── Linha 2: por evento + por pagamento ── */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.5fr 1fr" }, gap: 2, alignItems: "start" }}>
            {/* Por evento */}
            <Paper sx={glass}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <EventRoundedIcon sx={{ opacity: 0.6, fontSize: 18 }} />
                <Typography sx={{ fontWeight: 800 }}>Receita por evento</Typography>
              </Stack>

              <Stack spacing={1.6}>
                {byEvent.map((ev) => (
                  <Box key={ev.id}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.6 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 1 }}>
                        {ev.title}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                        <Chip
                          label={`${ev.tickets} ing.`}
                          size="small"
                          sx={{ height: 20, fontSize: "0.65rem", bgcolor: "rgba(105,240,174,0.12)", color: "#69f0ae", border: "none" }}
                        />
                        <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#40c4ff", minWidth: 80, textAlign: "right" }}>
                          {fmt(ev.revenue)}
                        </Typography>
                      </Stack>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={maxRevenue > 0 ? (ev.revenue / maxRevenue) * 100 : 0}
                      sx={{
                        height: 5,
                        borderRadius: 4,
                        bgcolor: "rgba(255,255,255,0.07)",
                        "& .MuiLinearProgress-bar": { bgcolor: "#40c4ff", borderRadius: 4 },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Paper>

            {/* Por método de pagamento */}
            <Paper sx={glass}>
              <Typography sx={{ fontWeight: 800, mb: 2 }}>Método de pagamento</Typography>

              {byPayment.length > 0 && (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={byPayment}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={75}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {byPayment.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RTooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <Box sx={{ background: "rgba(10,20,40,0.95)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "10px", p: 1.5 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{d.name}</Typography>
                            <Typography sx={{ fontSize: 12, opacity: 0.7 }}>{d.value} pedidos · {fmt(d.revenue)}</Typography>
                          </Box>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}

              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 1.5 }} />

              <Stack spacing={1}>
                {byPayment.map((p, i) => (
                  <Stack key={p.name} direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <Typography sx={{ fontSize: 13 }}>{p.name}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography sx={{ fontSize: 12, opacity: 0.6 }}>{p.value}x</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{fmt(p.revenue)}</Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Box>

          {/* ── Tipos de ingresso ── */}
          {byTicketType.length > 0 && (
            <Paper sx={glass}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <ConfirmationNumberRoundedIcon sx={{ opacity: 0.6, fontSize: 18 }} />
                <Typography sx={{ fontWeight: 800 }}>Vendas por tipo de ingresso</Typography>
              </Stack>

              {/* Mobile: cards */}
              <Stack spacing={1.2} sx={{ display: { xs: "flex", sm: "none" } }}>
                {byTicketType.map((t, i) => (
                  <Box key={t.name} sx={{ p: 1.5, borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{t.name}</Typography>
                      <Typography sx={{ fontWeight: 900, fontSize: 13, color: "#69f0ae" }}>{fmt(t.revenue)}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flex: 1, height: 5, borderRadius: 4, bgcolor: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
                        <Box sx={{ width: `${maxQty > 0 ? (t.qty / maxQty) * 100 : 0}%`, height: "100%", bgcolor: PIE_COLORS[i % PIE_COLORS.length], borderRadius: 4 }} />
                      </Box>
                      <Typography sx={{ fontSize: 11, opacity: 0.6, flexShrink: 0 }}>{t.qty} un.</Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>

              {/* Desktop: bar chart */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <ResponsiveContainer width="100%" height={Math.max(200, byTicketType.length * 48)}>
                  <BarChart
                    data={byTicketType}
                    layout="vertical"
                    margin={{ top: 0, right: 60, left: 0, bottom: 0 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fill: "rgba(255,255,255,0.75)", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      width={140}
                    />
                    <RTooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <Box sx={{ background: "rgba(10,20,40,0.95)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "10px", p: 1.5 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{d.name}</Typography>
                            <Typography sx={{ fontSize: 12, opacity: 0.7 }}>{d.qty} vendidos · {fmt(d.revenue)}</Typography>
                          </Box>
                        );
                      }}
                    />
                    <Bar dataKey="qty" radius={[0, 6, 6, 0]}>
                      {byTicketType.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}

          {/* ── Tabela por evento (detalhada) ── */}
          <Paper sx={glass}>
            <Typography sx={{ fontWeight: 800, mb: 2 }}>Resumo por evento</Typography>

            {/* Mobile: cards */}
            <Stack spacing={1.2} sx={{ display: { xs: "flex", md: "none" } }}>
              {byEvent.map((ev) => (
                <Box key={ev.id} sx={{ p: 1.5, borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 13, mb: 1 }}>{ev.title}</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.6 }}>
                    <Chip label={`${ev.orders} pedidos`} size="small" sx={chipSx} />
                    <Chip label={`${ev.tickets} ingressos`} size="small" sx={chipSx} />
                    <Chip label={fmt(ev.revenue)} size="small" sx={{ ...chipSx, color: "#40c4ff", bgcolor: "rgba(0,180,255,0.12)" }} />
                    <Chip
                      label={`Ticket médio ${fmt(ev.orders > 0 ? ev.revenue / ev.orders : 0)}`}
                      size="small"
                      sx={{ ...chipSx, color: "#ce93d8", bgcolor: "rgba(206,147,216,0.10)" }}
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>

            {/* Desktop: tabela */}
            <Box sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Evento", "Pedidos", "Ingressos", "Ticket médio", "Receita"].map((h) => (
                      <th key={h} style={{ textAlign: h === "Evento" ? "left" : "right", padding: "8px 12px", fontSize: 11, opacity: 0.5, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {byEvent.map((ev) => (
                    <tr key={ev.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ padding: "12px 12px", fontSize: 13, fontWeight: 700, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ev.title}
                      </td>
                      <td style={{ padding: "12px 12px", fontSize: 13, textAlign: "right", opacity: 0.8 }}>{ev.orders}</td>
                      <td style={{ padding: "12px 12px", fontSize: 13, textAlign: "right", opacity: 0.8 }}>{ev.tickets}</td>
                      <td style={{ padding: "12px 12px", fontSize: 13, textAlign: "right", opacity: 0.7 }}>
                        {fmt(ev.orders > 0 ? ev.revenue / ev.orders : 0)}
                      </td>
                      <td style={{ padding: "12px 12px", fontSize: 14, fontWeight: 900, textAlign: "right", color: "#40c4ff" }}>
                        {fmt(ev.revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Paper>
        </Stack>
      )}
    </Box>
  );
}

// ─── Sx helpers ───────────────────────────────────────────────────────────────

const selectSx = {
  borderRadius: "12px",
  background: "rgba(255,255,255,0.06)",
  color: "white",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.16)" },
  "& .MuiSvgIcon-root": { color: "white" },
};

const chipSx = {
  height: 20,
  fontSize: "0.65rem",
  bgcolor: "rgba(255,255,255,0.07)",
  border: "none",
};
